import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SocialShare } from '../components/SocialShare';
import axios from 'axios';
import { ArrowLeft, Clock, Tag, User } from 'lucide-react';
import { API } from '../config';


export default function BlogArticlePage() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(r => {
        setPost(r.data);
        // Update meta tags for SEO
        const p = r.data;
        if (p) {
          const t = p.meta_title || p.title || '';
          const d = p.meta_description || p.excerpt || '';
          document.title = t;
          const setMeta = (sel, val, attr = 'name') => {
            let el = document.querySelector(`meta[${attr}="${sel}"]`);
            if (!el) { el = document.createElement('meta'); el.setAttribute(attr, sel); document.head.appendChild(el); }
            el.setAttribute('content', val);
          };
          setMeta('description', d);
          setMeta('og:title', t, 'property');
          setMeta('og:description', d, 'property');
          setMeta('twitter:title', t);
          setMeta('twitter:description', d);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const backLabel = lang === 'fr' ? 'Retour au blog' : 'Back to blog';

  return (
    <div className="min-h-screen bg-background" data-testid="blog-article-page">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-white text-sm mb-8 transition-colors" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4" />
            {backLabel}
          </Link>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notFound ? (
            <div className="text-center py-20">
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">404</h2>
              <p className="text-muted-foreground">{lang === 'fr' ? 'Article introuvable.' : 'Article not found.'}</p>
            </div>
          ) : post && (
            <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="blog-article-content">
              {/* Meta */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString()}</span>
                {post.author_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />{post.author_name}</span>}
              </div>

              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight" data-testid="blog-article-title">
                {post.title}
              </h1>

              {post.excerpt && (
                <p className="text-muted-foreground text-lg mb-8 leading-relaxed">{post.excerpt}</p>
              )}

              {post.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {post.tags.map((tag, i) => (
                    <span key={i} className="flex items-center gap-1 px-3 py-1 rounded-full bg-secondary/60 border border-border/50 text-xs text-muted-foreground">
                      <Tag className="w-3 h-3" />{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Content · rendered as markdown-style paragraphs */}
              <div className="prose-custom space-y-5" data-testid="blog-article-body">
                {post.content.split('\n\n').map((paragraph, i) => {
                  if (paragraph.startsWith('## ')) {
                    return <h2 key={i} className="font-heading text-2xl font-semibold text-foreground mt-10 mb-4">{paragraph.replace('## ', '')}</h2>;
                  }
                  if (paragraph.startsWith('### ')) {
                    return <h3 key={i} className="font-heading text-xl font-medium text-foreground mt-8 mb-3">{paragraph.replace('### ', '')}</h3>;
                  }
                  if (paragraph.startsWith('- ')) {
                    const items = paragraph.split('\n').filter(l => l.startsWith('- '));
                    return (
                      <ul key={i} className="space-y-2 ml-4">
                        {items.map((item, j) => (
                          <li key={j} className="text-foreground/80 text-base leading-relaxed flex items-start gap-2">
                            <span className="text-blue-400 mt-1.5">•</span>
                            {item.replace('- ', '')}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return <p key={i} className="text-foreground/80 text-base leading-relaxed">{paragraph}</p>;
                })}
              </div>

              {/* Share */}
              <div className="mt-12 pt-8 border-t border-border">
                <SocialShare />
              </div>
            </motion.article>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
